import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { useCurrentUser, useLogout } from "@/features/auth";
import { useWorkerProfile } from "@/features/workers/model/useWorkerProfile";
import { AvailabilityStep } from "@/features/onboarding/ui/AvailabilityStep";
import { DocumentsStep } from "@/features/onboarding/ui/DocumentsStep";
import { EligibilityStep } from "@/features/onboarding/ui/EligibilityStep";
import { ExperienceStep } from "@/features/onboarding/ui/ExperienceStep";
import { IdentityStep } from "@/features/onboarding/ui/IdentityStep";
import { ReviewStep } from "@/features/onboarding/ui/ReviewStep";
import { StepIndicator } from "@/features/onboarding/ui/StepIndicator";
import { WelcomeStep } from "@/features/onboarding/ui/WelcomeStep";
import {
  ONBOARDING_STEPS,
  type OnboardingStep,
} from "@/features/onboarding/types";

export function OnboardingPage() {
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const { data: profile, isLoading } = useWorkerProfile();
  const [step, setStep] = useState<OnboardingStep>("WELCOME");

  // Resume at the first incomplete step on first load.
  useEffect(() => {
    if (!profile) return;
    if (step !== "WELCOME") return;
    if (!profile.legal_status) return; // start from WELCOME if user just signed up
    // Eligibility now also requires an uploaded document — drop them back
    // there if it's missing rather than letting them stall on Review.
    if (!profile.eligibility_document_url) {
      setStep("ELIGIBILITY");
    } else if (!profile.experience_level) {
      setStep("EXPERIENCE");
    } else if (!profile.photo_url) {
      // Photo is required to submit; drop them on DOCUMENTS instead of REVIEW
      // so they don't bounce off a missing-field error after clicking Submit.
      setStep("DOCUMENTS");
    } else {
      setStep("REVIEW");
    }
    // We only want to seed once on profile arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-copy-muted">Loading…</p>
      </div>
    );
  }
  if (!profile) {
    return <Navigate to="/login" replace />;
  }
  // If onboarding is already done, leave this route.
  if (profile.onboarding_completed_at) {
    return <Navigate to="/worker" replace />;
  }

  const goNext = () => {
    const idx = ONBOARDING_STEPS.indexOf(step);
    if (idx < ONBOARDING_STEPS.length - 1) {
      setStep(ONBOARDING_STEPS[idx + 1]);
    }
  };
  const goBack = () => {
    const idx = ONBOARDING_STEPS.indexOf(step);
    if (idx > 0) setStep(ONBOARDING_STEPS[idx - 1]);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <h1 className="text-base font-semibold tracking-display-tight text-copy">
            OneShift · Worker setup
          </h1>
          <button
            type="button"
            onClick={logout}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-copy-muted hover:bg-surface-sunken"
          >
            Sign out
          </button>
        </div>
        <div className="mx-auto max-w-3xl px-6 pb-4">
          <StepIndicator current={step} />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-8">
        <section className="rounded-lg border border-border bg-surface p-6 shadow-card">
          {step === "WELCOME" && (
            <WelcomeStep email={user?.email} onNext={goNext} />
          )}
          {step === "IDENTITY" && (
            <IdentityStep
              profile={profile}
              initialPhone={user?.phone ?? null}
              onSaved={goNext}
              onBack={goBack}
            />
          )}
          {step === "ELIGIBILITY" && (
            <EligibilityStep
              profile={profile}
              onSaved={goNext}
              onBack={goBack}
            />
          )}
          {step === "EXPERIENCE" && (
            <ExperienceStep
              profile={profile}
              onSaved={goNext}
              onBack={goBack}
            />
          )}
          {step === "AVAILABILITY" && (
            <AvailabilityStep
              profile={profile}
              onSaved={goNext}
              onBack={goBack}
            />
          )}
          {step === "DOCUMENTS" && (
            <DocumentsStep
              profile={profile}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {step === "REVIEW" && (
            <ReviewStep
              profile={profile}
              email={user?.email}
              phone={user?.phone ?? null}
              onBack={goBack}
            />
          )}
        </section>
      </main>
    </div>
  );
}
